/**
 * chatTurnState — AUTHORITATIVE definition of the transient per-turn state
 * cleared at every §4.1 teardown point in the FAR-chat containers
 * (`FarChat/FarChatPopup.tsx` and `HomeChat/HomeChatPanel.tsx`).
 *
 * Background (the bug class this guards against)
 * ----------------------------------------------
 * Both containers hold a handful of state fields that live ONLY for the
 * duration of a single in-flight turn / clarification round-trip:
 *
 *   - `inFlight`              — the turn-in-progress flag
 *   - `pendingRequestId`      — client_request_id the StepProgressChecklist
 *                               subscribes to
 *   - `inFlightUserMsg`       — optimistic user bubble text
 *   - `pendingClarification`  — the transient ClarificationEnvelope
 *   - `inFlightClarification` — the "Clarified: …" callout text
 *
 * §4.1 multi-turn state hygiene requires these be reset at teardown so a
 * stale `final_question` / pending clarification can never leak into the
 * next turn (which would make the backend skip the clarification gate, or
 * leak a previous session's bubble/checklist into a freshly-selected pane).
 *
 * Historically each container open-coded the reset set at each teardown
 * point, duplicated across two files. Adding a new transient field meant
 * remembering to clear it in ~8 places, and forgetting one reintroduced
 * the state-leak class. This module makes the reset set authoritative in
 * ONE place: adding a transient field here (and to `TRANSIENT_TURN_FIELDS`)
 * forces both containers to clear it.
 *
 * Two reset shapes
 * ----------------
 * The teardown points fall into exactly two categories that clear DIFFERENT
 * subsets — preserved here so the extraction is behavior-preserving:
 *
 *   1. FULL teardown (`resetTransientTurnState`) — navigation teardown and
 *      progress-complete. Clears ALL FIVE fields, INCLUDING
 *      `pendingClarification`. Call sites: both containers'
 *      `handleProgressComplete`; HomeChat's `handleNewChat` /
 *      `handleSelectSession` / `handleDelete`.
 *
 *   2. IN-FLIGHT teardown (`resetInFlightTurnState`) — the turn-lifecycle
 *      `finally` blocks. Clears the four in-flight fields but DELIBERATELY
 *      LEAVES `pendingClarification` untouched, because on the vague-query
 *      path `pendingClarification` was just set (to render the
 *      ClarificationCard) and must survive the finally. It is reset instead
 *      on the answer/resolve paths and at full teardown. Call sites:
 *      FarChat `submitTurn` finally; HomeChat `runPrimingTurn` / `handleSend`
 *      finally.
 *
 * `pendingRequestId` clearing varies by call site (plain setter vs. the
 * ref-syncing setter vs. HomeChat's `clearPendingIfOwned`), so it is passed
 * in as a callback rather than baked in — each call site keeps its EXACT
 * existing semantics (navigation uses the synced setter; progress-complete
 * deliberately uses the plain setter; the finally uses `clearPendingIfOwned`).
 */

import type {
  ClarificationEnvelope,
  TopicClarificationEnvelope,
} from './FarChat/types';

/**
 * The transient per-turn fields, enumerated once. This is the regression
 * tripwire: a new transient field added to a container's reset must be
 * added here too, and the shared-helper test asserts every name in this
 * list is cleared by `resetTransientTurnState`.
 */
export const TRANSIENT_TURN_FIELDS = [
  'inFlight',
  'pendingRequestId',
  'inFlightUserMsg',
  'pendingClarification',
  'inFlightClarification',
  // Post-reader topic-focus transient state (mirrors pendingClarification /
  // inFlightClarification). `pendingTopicClarification` holds the
  // TopicClarificationEnvelope while the TopicFocusCard is shown;
  // `inFlightTopicFocus` is the "Focused on: …" callout text shown while the
  // focused synth runs. Both MUST reset after the answer so the next fresh
  // query carries neither `focus_token` nor a stale focus
  // (post-reader spec §4.1 / §9 state hygiene).
  'pendingTopicClarification',
  'inFlightTopicFocus',
] as const;

export type TransientTurnField = (typeof TRANSIENT_TURN_FIELDS)[number];

/**
 * Setters for the transient turn state. `clearPendingRequestId` is the
 * call-site-specific mechanism for clearing `pendingRequestId` (plain
 * setter / ref-syncing setter / `clearPendingIfOwned`), invoked with no
 * argument so each container supplies the variant matching its teardown
 * semantics.
 */
export interface TransientTurnSetters {
  setInFlight: (v: boolean) => void;
  clearPendingRequestId: () => void;
  setInFlightUserMsg: (v: string | null) => void;
  setPendingClarification: (v: ClarificationEnvelope | null) => void;
  setInFlightClarification: (v: string | null) => void;
  setPendingTopicClarification: (v: TopicClarificationEnvelope | null) => void;
  setInFlightTopicFocus: (v: string | null) => void;
}

/**
 * IN-FLIGHT teardown (turn-lifecycle `finally`). Clears every transient
 * field EXCEPT `pendingClarification`, which must survive a vague-query
 * finally so the ClarificationCard renders. Mirrors the exact subset the
 * containers cleared in their finally blocks before this extraction.
 */
export function resetInFlightTurnState(
  setters: Pick<
    TransientTurnSetters,
    | 'setInFlight'
    | 'clearPendingRequestId'
    | 'setInFlightUserMsg'
    | 'setInFlightClarification'
    | 'setInFlightTopicFocus'
  >
): void {
  setters.setInFlight(false);
  setters.clearPendingRequestId();
  setters.setInFlightUserMsg(null);
  setters.setInFlightClarification(null);
  setters.setInFlightTopicFocus(null);
}

/**
 * FULL teardown (navigation + progress-complete). Clears ALL transient
 * fields including `pendingClarification` and `pendingTopicClarification`.
 * Authoritative: every field in `TRANSIENT_TURN_FIELDS` is reset here.
 */
export function resetTransientTurnState(setters: TransientTurnSetters): void {
  resetInFlightTurnState(setters);
  setters.setPendingClarification(null);
  setters.setPendingTopicClarification(null);
}
